import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { LoginForm } from "../components/auth/LoginForm";
import { SecurityVerification } from "../components/auth/SecurityVerification";
import { AuthService } from "../services/auth.service.real";
import { LoginSchemaType } from "../domain/validation/auth.schema";
import { User } from "../domain/types/auth.types";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { HttpError } from "../services/http.service";

type LoginStep = "login" | "mfa";

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<LoginStep>("login");
  const [userData, setUserData] = useState<User | null>(null);
  const [error, setError] = useState<string>("");

  const loginMutation = useMutation({
    mutationFn: (data: LoginSchemaType) => AuthService.login(data.email, data.password),
    onSuccess: (result) => {
      // Clear any previous errors
      setError("");

      if (result.requiresMFA) {
        setUserData(result.user);
        setStep("mfa");
      } else if (result.user && result.authToken) {
        // No MFA required, directly log in
        AuthService.saveCurrentUser(result.user);
        AuthService.saveAuthToken(result.authToken);
        navigate("/");
      }
    },
    onError: (err) => {
      console.error("Login failed:", err);
      if (err instanceof HttpError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred during login");
      }
    },
  });

  const verifyMFAMutation = useMutation({
    mutationFn: ({ method, code }: { method: "sms" | "email" | "authenticator"; code: string }) => {
      // Map to the correct API format
      const authenticatorCode = method === "authenticator" ? code : undefined;
      const emailCode = method === "email" ? code : undefined;
      const phoneCode = method === "sms" ? code : undefined;

      return AuthService.verifyMFA(authenticatorCode, emailCode, phoneCode);
    },
    onSuccess: (isValid) => {
      // Clear any previous errors
      setError("");

      if (isValid) {
        navigate("/");
      } else {
        setError("Invalid verification code. Please try again.");
      }
    },
    onError: (err) => {
      console.error("MFA verification failed:", err);
      if (err instanceof HttpError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred during verification");
      }
    },
  });

  const handleLogin = async (data: LoginSchemaType) => {
    // Clear any previous errors
    setError("");

    try {
      await loginMutation.mutateAsync(data);
    } catch (err) {
      // Error will be handled by onError callback in useMutation
      // This catch block prevents unhandled promise rejections
    }
  };

  const handleRequestCode = async (method: "sms" | "email") => {
    try {
      // Codes are sent automatically during login, this might just be a resend
      await AuthService.sendMFACode(method);
    } catch (err) {
      // Handle API errors for code resending
      console.error("Failed to send MFA code:", err);
      if (err instanceof HttpError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Failed to send verification code. Please try again.");
      }
    }
  };

  const handleVerify = async (method: "sms" | "email" | "authenticator", code?: string) => {
    if (!code) return;

    try {
      await verifyMFAMutation.mutateAsync({ method, code });
    } catch (err) {
      // Error will be handled by onError callback in useMutation
      // This catch block prevents unhandled promise rejections
    }
  };

  const handleConfirm = async (smsCode: string, emailCode: string, authenticatorCode: string) => {
    try {
      const isValid = await AuthService.verifyMFA(
        authenticatorCode || undefined,
        emailCode || undefined,
        smsCode || undefined
      );

      if (isValid) {
        navigate("/");
      } else {
        setError("Invalid verification codes. Please try again.");
      }
    } catch (err) {
      console.error("MFA verification failed:", err);
      if (err instanceof HttpError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred during verification");
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 font-arial bg-primary-blue">
      <div>
        {error && (
          <ErrorMessage message={error} className="mb-4 max-w-sm" onDismiss={() => setError("")} />
        )}

        {step === "login" && (
          <LoginForm onSubmit={handleLogin} isLoading={loginMutation.isPending} />
        )}

        {step === "mfa" && (
          <SecurityVerification
            phone={userData?.phone || ""}
            email={userData?.email || ""}
            googleAuthEnabled={true}
            onVerify={handleVerify}
            onRequestCode={handleRequestCode}
            onConfirm={handleConfirm}
            isLoading={verifyMFAMutation.isPending}
          />
        )}
      </div>
    </div>
  );
};
